import { SelectHTMLAttributes, forwardRef } from 'react';

import { cn } from '../utils/cn';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  options: SelectOption[];
  placeholder?: string;
  size?: 'sm' | 'md' | 'lg';
  error?: boolean;
  label?: string;
  helperText?: string;
}

export const Select = forwardRef<HTMLSelectElement, SelectProps>(
  (
    {
      className,
      options,
      placeholder,
      size = 'md',
      error = false,
      label,
      helperText,
      id,
      ...props
    },
    ref
  ) => {
    const inputId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

    return (
      <div className="w-full">
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-[color:var(--ids-text,#F8FAFC)] mb-1.5"
          >
            {label}
          </label>
        )}
        <div className="relative">
          <select
            ref={ref}
            id={inputId}
            className={cn(
              'w-full appearance-none rounded-[var(--ids-radius-md,10px)] border bg-[color:var(--ids-bg-subtle,#0F1626)] text-[color:var(--ids-text,#F8FAFC)]',
              'transition-colors duration-200',
              'focus:outline-none focus:ring-2 focus:ring-offset-0 focus:bg-[color:var(--ids-surface,#141B2D)]',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              {
                // Sizes
                'px-3 py-1.5 text-sm pr-8': size === 'sm',
                'px-4 py-2 text-base pr-10': size === 'md',
                'px-4 py-3 text-lg pr-12': size === 'lg',
                // States
                'border-[color:var(--ids-border,rgba(255,255,255,0.08))] focus:border-[color:var(--ids-brand,#3B82F6)] focus:ring-[color:rgba(var(--ids-brand-rgb,59,130,246),0.18)]':
                  !error,
                'border-[color:var(--ids-danger,#EF4444)] focus:border-[color:var(--ids-danger,#EF4444)] focus:ring-[color:rgba(var(--ids-danger-rgb,239,68,68),0.16)]':
                  error,
              },
              className
            )}
            {...props}
          >
            {placeholder && (
              <option value="" disabled>
                {placeholder}
              </option>
            )}
            {options.map((option) => (
              <option key={option.value} value={option.value} disabled={option.disabled}>
                {option.label}
              </option>
            ))}
          </select>
          <div
            className={cn('absolute inset-y-0 right-0 flex items-center pointer-events-none', {
              'pr-2': size === 'sm',
              'pr-3': size === 'md',
              'pr-4': size === 'lg',
            })}
          >
            <svg
              className={cn('text-[color:var(--ids-text-subtle,#7E8DA6)]', {
                'w-4 h-4': size === 'sm',
                'w-5 h-5': size === 'md' || size === 'lg',
              })}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 9l-7 7-7-7"
              />
            </svg>
          </div>
        </div>
        {helperText && (
          <p
            className={cn('mt-1.5 text-sm', {
              'text-[color:var(--ids-text-subtle,#7E8DA6)]': !error,
              'text-[color:var(--ids-danger,#EF4444)]': error,
            })}
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Select.displayName = 'Select';
